import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Dialog, DialogContent, DialogActions, IconButton, Stack,
    RadioGroup, Radio, FormControlLabel, TextField, MenuItem, Alert, CircularProgress,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import http, { apiErrorMessage } from '../Api/http';
import { BulkCreateEmployeeLogins, GetUserTypes } from '../Api/Api';
import { canAssignRole, ROLE } from '../data/roleAccess';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_BORDER = '#B3D3E8';
const cell = { px: 1.4, py: 0.8, fontSize: 12.5, borderBottom: '1px solid #EEF0F6', whiteSpace: 'nowrap' };

/**
 * Creates logins for every active employee that has none.
 * Username = Employee ID (company id) else system code; password is either each
 * employee's own company id or one shared password.
 */
export default function BulkLoginsDialog({ open, onClose, myUserTypeId, onDone }) {
    const [mode, setMode] = useState('companyId');
    const [shared, setShared] = useState({ password: '', confirmPassword: '' });
    const [userTypeId, setUserTypeId] = useState(ROLE.EMPLOYEE);
    const [roles, setRoles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setResult(null); setError('');
        http.get(GetUserTypes)
            .then(({ data: body }) => {
                const list = Array.isArray(body?.data?.roles) ? body.data.roles : [];
                setRoles(list.filter((r) => canAssignRole(myUserTypeId, r.id)));
            })
            .catch(() => { });
    }, [open, myUserTypeId]);

    const submit = async () => {
        setError('');
        const useId = mode === 'companyId';
        // With one shared password the server needs both fields; with company ids
        // a fallback password is optional (employees without an id are skipped otherwise).
        if (!useId || shared.password || shared.confirmPassword) {
            if (shared.password !== shared.confirmPassword) { setError('Passwords do not match.'); return; }
            if (!useId && shared.password.length < 8) { setError('Shared password must be at least 8 characters.'); return; }
        }
        const body = { userTypeId: Number(userTypeId) || ROLE.EMPLOYEE };
        if (useId) body.useCompanyEmployeeIdAsPassword = true;
        if (shared.password) { body.defaultPassword = shared.password; body.confirmPassword = shared.confirmPassword; }

        setBusy(true);
        try {
            const { data } = await http.post(BulkCreateEmployeeLogins, body);
            if (data?.error) throw new Error(data.message || 'Could not create logins.');
            setResult(data?.data || {});
            onDone?.(data?.data);
        } catch (err) {
            setError(err?.response || err?.request ? apiErrorMessage(err, 'Could not create logins.') : err.message);
        } finally {
            setBusy(false);
        }
    };

    const logins = result?.logins || [];
    const skipped = result?.skipped || [];

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '7px' } } }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${PRIMARY_BORDER}`, background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '7px', bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <KeyRoundedIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>Create logins in bulk</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.3 }}>For every active employee who doesn&apos;t have a login yet</Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} disabled={busy} size="small"><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>

            <DialogContent sx={{ p: 2.5 }}>
                {!result ? (
                    <>
                        <Typography sx={{ fontSize: 12.5, color: '#475569', mb: 1.5 }}>
                            The username is each employee&apos;s <strong>Employee ID</strong> (or their system code when none is set).
                        </Typography>
                        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                            <FormControlLabel value="companyId" control={<Radio size="small" />}
                                label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Use each employee&apos;s Employee ID as the starting password <Box component="span" sx={{ color: '#16A34A', fontWeight: 700 }}>(recommended)</Box></Typography>} />
                            <FormControlLabel value="shared" control={<Radio size="small" />}
                                label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>One shared password for everyone</Typography>} />
                        </RadioGroup>

                        {(mode === 'shared' || mode === 'companyId') && (
                            <Stack spacing={1.4} sx={{ mt: 1.5 }}>
                                {mode === 'companyId' && (
                                    <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>
                                        Optional fallback password for employees with no Employee ID (they are skipped if you leave it empty).
                                    </Typography>
                                )}
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
                                    <TextField size="small" fullWidth type="password" label={mode === 'shared' ? 'Password' : 'Fallback password'} value={shared.password} onChange={(e) => setShared((s) => ({ ...s, password: e.target.value }))} />
                                    <TextField size="small" fullWidth type="password" label="Confirm password" value={shared.confirmPassword} onChange={(e) => setShared((s) => ({ ...s, confirmPassword: e.target.value }))} />
                                </Stack>
                            </Stack>
                        )}

                        {roles.length > 0 && (
                            <TextField select size="small" fullWidth label="Role" value={userTypeId} onChange={(e) => setUserTypeId(e.target.value)} sx={{ mt: 2 }}>
                                {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                            </TextField>
                        )}

                        {mode === 'companyId' && (
                            <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                                The seeded password is a handout credential and skips the 8-character rule — ask employees to change it after first sign-in.
                            </Alert>
                        )}
                        {error && <Alert severity="error" sx={{ mt: 2, fontSize: 12.5 }}>{error}</Alert>}
                    </>
                ) : (
                    <>
                        <Alert severity={result.created > 0 ? 'success' : 'warning'} sx={{ mb: 1.5, fontSize: 13 }}>
                            {result.created ?? 0} login{result.created === 1 ? '' : 's'} created{skipped.length ? `, ${skipped.length} skipped` : ''}.
                        </Alert>
                        {logins.length > 0 && (
                            <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid #E6EAF1', borderRadius: '7px', mb: 1.5 }}>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <Box component="thead" sx={{ bgcolor: '#F3F8FC', position: 'sticky', top: 0 }}>
                                        <Box component="tr">{['Name', 'Username', 'System code'].map((h) => <Box component="th" key={h} sx={{ ...cell, textAlign: 'left', fontSize: 10.5, color: '#5B7A94', fontWeight: 700 }}>{h}</Box>)}</Box>
                                    </Box>
                                    <Box component="tbody">
                                        {logins.map((l) => (
                                            <Box component="tr" key={l.employeeCode}>
                                                <Box component="td" sx={cell}>{l.name}</Box>
                                                <Box component="td" sx={{ ...cell, fontWeight: 700, color: PRIMARY }}>{l.userName}</Box>
                                                <Box component="td" sx={{ ...cell, color: '#6B7280' }}>{l.employeeCode}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {skipped.length > 0 && (
                            <Box sx={{ maxHeight: 160, overflow: 'auto', border: '1px solid #FED7AA', bgcolor: '#FFF7ED', borderRadius: '7px', p: 1.2 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#C2410C', textTransform: 'uppercase', mb: 0.5 }}>Skipped</Typography>
                                {skipped.map((s) => (
                                    <Typography key={s.employeeCode} sx={{ fontSize: 12, color: '#7C2D12' }}>{s.employeeCode} — {s.reason}</Typography>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.6, borderTop: '1px solid #E5E7EB' }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600, color: '#374151' }}>{result ? 'Close' : 'Cancel'}</Button>
                {!result && (
                    <Button variant="contained" disableElevation onClick={submit} disabled={busy}
                        startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, borderRadius: '7px', '&:hover': { bgcolor: '#0E4A75' } }}>
                        {busy ? 'Creating…' : 'Create logins'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
